import { useContext } from 'react';
import { Button, Tooltip } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useSnapshot } from 'valtio';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { toast } from '@duke-hq/app-admin-web/components/ToastContainer';
import { useOptimisticPublishTranslations } from '../components/translations/useOptimisticPublishTranslations';
import { FlowEditorStateContext } from '../components/ReactFlow/FlowEditorStateContext';
import { flowEditorState } from '../components/ReactFlow/flowEditorState';
import { validateFlowEditorState } from './validateFlowEditorState';

/**
 * Publishes the current conversation flow. Validates the flow first and
 * surfaces validation errors as a toast. Only enabled once the draft is saved.
 *
 * Disabled when the tab is stale: publish snapshots the draft as it stands on
 * the server, so publishing from a stale tab would ship content the user never
 * reviewed. `getBaseRevision` reads the revision the editor last saved from, so
 * the server can reject a publish if the draft has since moved on.
 */
export function FlowPublishButton({
  isStale = false,
  getBaseRevision,
}: {
  isStale?: boolean;
  getBaseRevision: () => number;
}) {
  const { setLastPublishedResourceVersion } = useContext(FlowEditorStateContext);
  const writeOptimisticTranslations = useOptimisticPublishTranslations();
  const utils = trpcAdmin.useUtils();
  const publish = trpcAdmin.resourceVersions.publish.useMutation();
  const flowEditorSnapshot = useSnapshot(flowEditorState);
  const saved = !flowEditorSnapshot.isDirty;

  return (
    <Tooltip label={t`Load the latest version before publishing`} isDisabled={!isStale}>
      <Button
        variant="primary"
        size="sm"
        isDisabled={!saved || isStale}
        isLoading={publish.isPending}
        onClick={async () => {
          if (!flowEditorSnapshot.flowEditor) {
            throw new Error('No flow editor in state');
          }
          const flowState = flowEditorSnapshot.flowEditor.toObject();

          const result = validateFlowEditorState(flowState);
          if (!result.isValid) {
            toast({
              status: 'error',
              title: t`Flow cannot be published`,
              description: result.message,
            });
            return;
          }

          const resourceVersionId = flowEditorSnapshot.resource?.draftVersion?.id;
          if (!resourceVersionId) {
            toast({
              status: 'error',
              title: t`Flow cannot be published`,
              description: t`No draft version to publish.`,
            });
            return;
          }

          const res = await publish.mutateAsync({
            resourceVersionId,
            baseRevision: getBaseRevision(),
          });
          setLastPublishedResourceVersion(res);
          // Refetch the resource so the toolbar's translations pill repoints to
          // the newly published version rather than staying on the old one.
          void utils.resources.byId.invalidate(res.resource_id);
          // Publishing kicks off per-language translation rows for the new
          // version. Write optimistic PENDING rows for the new version straight
          // into the cache so the toolbar dot and panel begin streaming in the
          // instant you publish, then resume the poll to reconcile with the server.
          writeOptimisticTranslations(res.id, 'CONVERSATION');
          toast({
            status: 'success',
            title: t`Version ${res.version} published`,
          });
        }}
      >
        <Trans>Publish</Trans>
      </Button>
    </Tooltip>
  );
}
